import { cn } from '@/lib/utils';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { ComponentPropsWithoutRef, ComponentRef, forwardRef } from 'react';

type SwitchProps = ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> & {
  height?: number;
  checkedColor?: string;
  uncheckedColor?: string;
};

const Switch = forwardRef<ComponentRef<typeof SwitchPrimitive.Root>, SwitchProps>(
  ({ className, height = 20, checkedColor, uncheckedColor, disabled, ...props }, ref) => {
    // Calculate proportional dimensions based on height
    const width = Math.round(height * 1.8); // Maintain 1.8:1 width to height ratio
    const thumbSize = Math.round(height * 0.7); // Thumb is 80% of height
    const thumbOffset = Math.round((width - thumbSize) * 0.7); // 90% of available space for translation
    const holeSize = Math.round(thumbSize * 0.35); // Hole is 35% of thumb size

    return (
      <SwitchPrimitive.Root
        ref={ref}
        className={cn(
          'relative inline-flex items-center rounded-full border-2 border-transparent transition-colors',
          'focus:outline-none',
          'bg-input data-[state=checked]:bg-switch-checked data-[state=unchecked]:bg-switch-unchecked',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
        style={{
          height: `${height}px`,
          width: `${width}px`,
          backgroundColor: props.checked ? checkedColor : uncheckedColor,
        }}
        disabled={disabled}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'relative block rounded-full bg-white transition-transform',
            'translate-x-0.5 after:absolute after:left-1/2 after:top-1/2',
            'after:block after:rounded-full after:bg-[#E5E7EB]',
            'after:-translate-x-1/2 after:-translate-y-1/2',
            'after:transition-colors',
            'after:bg-input data-[state=checked]:after:bg-primary'
          )}
          style={{
            height: `${thumbSize}px`,
            width: `${thumbSize}px`,
            transform: `translateX(${props.checked ? thumbOffset : 2}px)`,
            ['&::after' as string]: {
              width: `${holeSize}px`,
              height: `${holeSize}px`,
              backgroundColor: props.checked && checkedColor ? checkedColor : undefined,
            },
          }}
        />
      </SwitchPrimitive.Root>
    );
  }
);
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
